import { useAuth } from '@clerk/clerk-react';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { workflowsApi } from '~/api/def/servers/defaultServer';
import {
  Notification,
  notificationsAtom,
  notificationSchema,
  NotificationsState,
} from '~/atoms/workflowsSimpleAtom';
import {
  createSSEConnection,
  SSEConnection,
  SSEConnectionConfig,
  subscribe,
} from './sseConnection';

const sseConfig = (
  page: number,
  pageSize?: number,
): SSEConnectionConfig<Notification, Notification> => ({
  url: `${workflowsApi.url}/workflows?format=simple&page=${page}&page_size=${pageSize}`,
  schema: notificationSchema,
  dataConverter: (data) => data,
});

let connection: SSEConnection<Notification, Notification> | null = null;

const getConnection = <TNotification>() => {
  if (!connection) {
    connection = createSSEConnection<TNotification>();
  }
  return connection;
};

/**
 * Efficiently merges a new notification into the existing notifications array.
 * Replaces notifications with the same workflow_id only if the new one has a more recent timestamp.
 * Maintains newest notifications at the beginning of the array.
 *
 * @param notifications - The existing notifications array
 * @param newNotification - The new notification to possibly add
 * @param pageSize - Optional limit for the number of notifications to keep
 * @returns A new array with the merged notifications
 */
const mergeNotificationByMostRecent = (
  notifications: Notification[],
  newNotification: Notification,
  pageSize?: number,
): Notification[] => {
  const workflowId = newNotification.workflow_id;

  const existingIndex = notifications.findIndex(
    (n) => n.workflow_id === workflowId,
  );

  let result: Notification[];

  if (existingIndex === -1) {
    result = [newNotification, ...notifications];
  } else {
    const existingNotification = notifications[existingIndex];

    // Always update notifications when status changes, especially to completed or failed
    if (existingNotification.status !== newNotification.status) {
      result = [...notifications];
      result.splice(existingIndex, 1);
      result = [newNotification, ...result];
    } else {
      // Check if task states have changed
      const existingTaskStatesStr = JSON.stringify(
        existingNotification.task_states,
      );
      const newTaskStatesStr = JSON.stringify(newNotification.task_states);

      if (existingTaskStatesStr !== newTaskStatesStr) {
        result = [...notifications];
        result.splice(existingIndex, 1);
        result = [newNotification, ...result];
      } else {
        // For same status and task states, only update if newer timestamp
        const existingTimestamp = new Date(
          existingNotification.started_at,
        ).getTime();
        const newTimestamp = new Date(newNotification.started_at).getTime();

        if (newTimestamp <= existingTimestamp) {
          return notifications;
        }

        result = [...notifications];
        result.splice(existingIndex, 1);
        result = [newNotification, ...result];
      }
    }
  }

  // If pageSize is specified, limit the number of notifications
  if (pageSize && result.length > pageSize) {
    return result.slice(0, pageSize);
  }

  return result;
};

export const useWorkflowsSimple = ({
  page = 0,
  pageSize = 1000,
}: {
  page?: number;
  pageSize?: number;
} = {}) => {
  const { getToken } = useAuth();
  const connection = getConnection<Notification>();
  const [, setNotifications] = useAtom(notificationsAtom);

  useEffect(() => {
    const unsubscribe = subscribe(
      connection,
      sseConfig(page, pageSize),
      (data) => {
        setNotifications((prev: NotificationsState) => {
          const updatedNotifications = mergeNotificationByMostRecent(
            prev.notifications,
            data,
            pageSize,
          );

          if (updatedNotifications === prev.notifications) {
            return prev;
          }

          return {
            ...prev,
            notifications: updatedNotifications,
          };
        });
      },
      (error) => {
        setNotifications((prev: NotificationsState) => ({
          ...prev,
          error,
        }));
      },
      (loading) => {
        setNotifications((prev: NotificationsState) => ({
          ...prev,
          isLoading: loading,
        }));
      },
      getToken,
      'notifications',
    );

    return () => {
      unsubscribe();
    };
  }, [page, pageSize]);
};
